import { useState, useEffect } from "react";
import { ExternalLink, Heart, Mail, Share2, X, Facebook, Linkedin, Link as LinkIcon, Check } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import axios from "axios";
import { API } from "@/config";
import { NewsletterModal } from "./NewsletterModal";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [version, setVersion] = useState("1.0.0");
  const [linkDonar, setLinkDonar] = useState("");
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Share handlers — shareUrl/shareTitle leídos en el momento del click para evitar stale en SPA
  const shareWhatsApp = () => {
    const shareUrl = window.location.href;
    const shareTitle = document.title;
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`, "_blank");
  };
  const shareTwitter = () => {
    const shareUrl = window.location.href;
    const shareTitle = document.title;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  };
  const shareFacebook = () => {
    const shareUrl = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
  };
  const shareLinkedIn = () => {
    const shareUrl = window.location.href;
    const shareTitle = document.title;
    window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`, "_blank");
  };

  const copyLink = async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config`);
        if (response.data?.version) {
          setVersion(response.data.version);
        }
        if (response.data?.link_donar) {
          setLinkDonar(response.data.link_donar);
        }
      } catch (error) {
        // Use default version if config not available
      }
    };
    fetchConfig();
  }, []);

  return (
    <>
      <footer className="bg-primary text-primary-foreground py-6 mt-auto" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Mobile Share Buttons */}
          <div className="md:hidden flex flex-col items-center mb-6 pb-6 border-b border-white/20">
            <span className="flex items-center gap-2 text-sm font-semibold mb-3">
              <Share2 className="h-4 w-4 text-accent" />
              Compartir esta página
            </span>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <Button onClick={shareWhatsApp} variant="outline" size="icon" className="h-9 w-9 bg-white/10 text-white hover:bg-green-500 hover:text-white border-none" title="WhatsApp">
                <FaWhatsapp className="h-4 w-4" />
              </Button>
              <Button onClick={shareTwitter} variant="outline" size="icon" className="h-9 w-9 bg-white/10 text-white hover:bg-sky-500 hover:text-white border-none" title="Twitter/X">
                <X className="h-4 w-4" />
              </Button>
              <Button onClick={shareFacebook} variant="outline" size="icon" className="h-9 w-9 bg-white/10 text-white hover:bg-blue-600 hover:text-white border-none" title="Facebook">
                <Facebook className="h-4 w-4" />
              </Button>
              <Button onClick={shareLinkedIn} variant="outline" size="icon" className="h-9 w-9 bg-white/10 text-white hover:bg-blue-700 hover:text-white border-none" title="LinkedIn">
                <Linkedin className="h-4 w-4" />
              </Button>
              <Separator orientation="vertical" className="h-6 bg-white/20" />
              <Button onClick={copyLink} variant="outline" size="sm" className="gap-2 text-xs bg-white/10 text-white hover:bg-white hover:text-primary border-none">
                {copied ? <Check className="h-3 w-3 text-green-400" /> : <LinkIcon className="h-3 w-3" />}
                {copied ? "¡Copiado!" : "Copiar"}
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo + Donar */}
            <div className="flex items-center gap-2">
              <img
                src="/logo.svg"
                alt="Movilidad Eléctrica"
                className="h-5 w-5"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className="font-semibold">Movilidad Eléctrica</span>
              {linkDonar && (
                <>
                  <span className="text-white/50">|</span>
                  <a
                    href={linkDonar}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline inline-flex items-center gap-1 font-medium"
                    data-testid="donar-link"
                  >
                    <Heart className="h-4 w-4" />
                    Donar
                  </a>
                </>
              )}

              {/* Newsletter */}
              <span className="text-white/50">|</span>
              <button
                onClick={() => setNewsletterOpen(true)}
                className="text-accent hover:underline inline-flex items-center gap-1 font-medium"
                data-testid="footer-newsletter"
              >
                <Mail className="h-4 w-4" />
                Newsletter
              </button>
            </div>

            {/* Copyright */}
            <div className="text-center md:text-right">
              <p className="text-sm text-white/80">
                © {currentYear} Desarrollado por:{" "}
                <a
                  href="https://christiannieves.uy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-accent hover:underline inline-flex items-center gap-1"
                >
                  Christian Nieves
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
              <p className="text-xs text-white/60 mt-1">
                Versión {version}
              </p>
            </div>
          </div>
        </div>
      </footer>

      <NewsletterModal
        isOpen={newsletterOpen}
        onClose={() => setNewsletterOpen(false)}
        listaTipo="general"
      />
    </>);
};
